import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const FEATURE_ICONS = {
  screenshots: '📸', app_tracking: '📱', website_tracking: '🌐',
  content_alerts: '🚨', screen_time: '⏰', bedtime: '🌙',
  hide_icon: '🔒', whatsapp_alerts: '💬', weekly_reports: '📊',
  pdf_reports: '📄', blocked_websites: '🚫', custom_keywords: '🔤',
  setup_codes: '🔑', multi_kid: '👶',
};

export default function Features() {
  const [features,   setFeatures]   = useState([]);
  const [customers,  setCustomers]  = useState([]);
  const [overrides,  setOverrides]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [toast,      setToast]      = useState('');
  const [selectedCust, setSelectedCust] = useState(null);
  const [search,     setSearch]     = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: feat }, { data: cust }, { data: ovr }] = await Promise.all([
      supabase.from('feature_flags').select('*').order('created_at', { ascending: true }),
      supabase.from('customers').select('id,name,email,plan').order('name', { ascending: true }),
      supabase.from('customer_feature_overrides').select('*'),
    ]);
    setFeatures(feat || []);
    setCustomers(cust || []);
    setOverrides(ovr || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Toggle global feature
  const toggleGlobal = async (feature) => {
    const newVal = !feature.enabled;
    await supabase.from('feature_flags').update({ enabled: newVal }).eq('id', feature.id);
    setFeatures(prev => prev.map(f => f.id === feature.id ? { ...f, enabled: newVal } : f));
    showToast(`${feature.label} ${newVal ? 'enabled' : 'disabled'} globally`);
  };

  // Get customer override for a feature
  const getOverride = (custId, featureKey) => {
    return overrides.find(o => o.customer_id === custId && o.feature_key === featureKey);
  };

  // Cycle override: Default → Force ON → Force OFF → Default
  const cycleOverride = async (custId, featureKey) => {
    const existing = getOverride(custId, featureKey);

    if (!existing) {
      // No override → create Force ON
      const { data } = await supabase.from('customer_feature_overrides')
        .insert({ customer_id: custId, feature_key: featureKey, enabled: true })
        .select();
      if (data?.[0]) setOverrides(prev => [...prev, data[0]]);
      showToast('Override: Force ON');
    } else if (existing.enabled === true) {
      // Force ON → Force OFF
      await supabase.from('customer_feature_overrides')
        .update({ enabled: false }).eq('id', existing.id);
      setOverrides(prev => prev.map(o => o.id === existing.id ? { ...o, enabled: false } : o));
      showToast('Override: Force OFF');
    } else {
      // Force OFF → Remove (back to Default)
      await supabase.from('customer_feature_overrides')
        .delete().eq('id', existing.id);
      setOverrides(prev => prev.filter(o => o.id !== existing.id));
      showToast('Override removed (using global default)');
    }
  };

  // Filtered customers for search
  const filteredCust = search.trim()
    ? customers.filter(c =>
        c.name?.toLowerCase().includes(search.toLowerCase()) ||
        c.email?.toLowerCase().includes(search.toLowerCase())
      )
    : customers;

  // Count overrides per customer
  const overrideCount = (custId) => overrides.filter(o => o.customer_id === custId).length;

  if (loading) return <div style={{ color: '#64748b', padding: 40 }}>Loading features...</div>;

  return (
    <div>
      <h1 className="page-title" style={{ marginBottom: 20 }}>🎛️ Feature Flags</h1>

      {/* Stats */}
      <div className="stat-row" style={{ marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-label">Total Features</div>
          <div className="stat-value blue">{features.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Enabled Globally</div>
          <div className="stat-value green">{features.filter(f => f.enabled).length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Disabled Globally</div>
          <div className="stat-value red">{features.filter(f => !f.enabled).length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Customer Overrides</div>
          <div className="stat-value yellow">{overrides.length}</div>
        </div>
      </div>

      {/* Section 1: Global Feature Flags */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-title" style={{ marginBottom: 16 }}>🌍 Global Features</div>
        <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>
          These control features for ALL customers. Turn off to disable a feature everywhere (unless a customer has a "Force ON" override).
        </p>
        <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <th>Feature</th>
              <th>Description</th>
              <th style={{ width: 80 }}>Overrides</th>
              <th style={{ width: 100 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {features.map(f => {
              const ovrCount = overrides.filter(o => o.feature_key === f.key).length;
              return (
                <tr key={f.id}>
                  <td style={{ fontSize: 20, textAlign: 'center' }}>{FEATURE_ICONS[f.key] || '⚙️'}</td>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{f.label}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>{f.key}</div>
                  </td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{f.description}</td>
                  <td style={{ textAlign: 'center' }}>
                    {ovrCount > 0 ? (
                      <span className="badge badge-yellow">{ovrCount}</span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => toggleGlobal(f)}
                      style={{
                        padding: '6px 16px', border: 'none', borderRadius: 99,
                        fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        background: f.enabled ? '#dcfce7' : '#f1f5f9',
                        color: f.enabled ? '#16a34a' : '#94a3b8',
                        transition: 'all 0.2s',
                      }}>
                      {f.enabled ? '🟢 ON' : '⚫ OFF'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Section 2: Per-Customer Overrides */}
      <div className="card">
        <div className="card-title" style={{ marginBottom: 16 }}>👤 Per-Customer Overrides</div>
        <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>
          Override features for specific customers. Useful for beta testing or giving premium features to select users.
        </p>

        {/* Customer selector */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <input type="text" placeholder="🔍 Search customer..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 200, maxWidth: 300, padding: '8px 12px', fontSize: 13, borderRadius: 8, border: '1px solid #e2e8f0' }} />
        </div>

        {/* Customer list with overrides */}
        {!selectedCust ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {filteredCust.map(c => (
              <button key={c.id}
                onClick={() => setSelectedCust(c)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', background: '#f8fafc',
                  border: '1px solid #e2e8f0', borderRadius: 8,
                  cursor: 'pointer', textAlign: 'left', width: '100%',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}>
                <span style={{ fontSize: 18 }}>👤</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{c.email}</div>
                </div>
                <span className={`badge badge-${c.plan === 'plus' ? 'blue' : c.plan === 'family' ? 'purple' : c.plan === 'trial' ? 'orange' : 'gray'}`}>
                  {c.plan}
                </span>
                {overrideCount(c.id) > 0 && (
                  <span className="badge badge-yellow">{overrideCount(c.id)} overrides</span>
                )}
              </button>
            ))}
            {filteredCust.length === 0 && (
              <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 20 }}>
                {search ? `No customers matching "${search}"` : 'No customers yet'}
              </p>
            )}
          </div>
        ) : (
          <>
            {/* Selected customer header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
              background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10,
              marginBottom: 16,
            }}>
              <span style={{ fontSize: 24 }}>👤</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{selectedCust.name}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{selectedCust.email}</div>
              </div>
              <span className={`badge badge-${selectedCust.plan === 'plus' ? 'blue' : selectedCust.plan === 'family' ? 'purple' : selectedCust.plan === 'trial' ? 'orange' : 'gray'}`}>
                {selectedCust.plan}
              </span>
              <button className="btn-sm" onClick={() => setSelectedCust(null)}>
                ← Back to list
              </button>
            </div>

            {/* Feature overrides grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
              {features.map(f => {
                const ovr = getOverride(selectedCust.id, f.key);
                const globalEnabled = f.enabled;
                let status, statusColor, statusBg, statusLabel;

                if (!ovr) {
                  // Default — follows global
                  status = 'default';
                  statusColor = '#64748b';
                  statusBg = '#f1f5f9';
                  statusLabel = globalEnabled ? 'Default (ON)' : 'Default (OFF)';
                } else if (ovr.enabled) {
                  // Force ON
                  status = 'force_on';
                  statusColor = '#16a34a';
                  statusBg = '#dcfce7';
                  statusLabel = 'Force ON';
                } else {
                  // Force OFF
                  status = 'force_off';
                  statusColor = '#dc2626';
                  statusBg = '#fee2e2';
                  statusLabel = 'Force OFF';
                }

                // Effective state
                const effective = ovr ? ovr.enabled : globalEnabled;

                return (
                  <div key={f.key} style={{
                    padding: '14px 16px', border: `1.5px solid ${effective ? '#86efac' : '#e2e8f0'}`,
                    borderRadius: 10, background: effective ? '#fafffe' : '#fafafa',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 18 }}>{FEATURE_ICONS[f.key] || '⚙️'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{f.label}</div>
                      </div>
                      <div style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: effective ? '#22c55e' : '#d1d5db',
                      }} />
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10 }}>{f.description}</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '3px 10px',
                        borderRadius: 99, background: statusBg, color: statusColor,
                      }}>
                        {statusLabel}
                      </span>
                      <button
                        onClick={() => cycleOverride(selectedCust.id, f.key)}
                        style={{
                          padding: '4px 12px', border: '1px solid #e2e8f0',
                          borderRadius: 6, fontSize: 11, fontWeight: 600,
                          cursor: 'pointer', background: '#fff', color: '#374151',
                        }}>
                        {status === 'default' ? 'Force ON →' : status === 'force_on' ? 'Force OFF →' : 'Reset →'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Planned Features */}
      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-title" style={{ marginBottom: 4 }}>Planned Feature Flags</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
          These features are in development and will be added as toggleable feature flags
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
          {[
            { icon: '📍', label: 'Location Tracking', platform: 'Android · iOS' },
            { icon: '📵', label: 'App Blocking', platform: 'All platforms' },
            { icon: '📲', label: 'Call & SMS Monitor', platform: 'Android' },
            { icon: '🔐', label: 'Remote Lock', platform: 'All platforms' },
            { icon: '📺', label: 'YouTube Monitoring', platform: 'All platforms' },
            { icon: '🗺️', label: 'Geofencing', platform: 'Android · iOS' },
            { icon: '💬', label: 'Social Media Monitor', platform: 'Android · iOS' },
            { icon: '🆘', label: 'SOS Button', platform: 'Android · iOS' },
            { icon: '🎥', label: 'Screen Recording', platform: 'macOS · Windows' },
            { icon: '👁️', label: 'Live Screen View', platform: 'All platforms' },
          ].map(f => (
            <div key={f.label} style={{
              background: '#f8fafc', border: '2px dashed #cbd5e1',
              borderRadius: 10, padding: '12px 14px', opacity: 0.8,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 18 }}>{f.icon}</span>
                <span style={{ fontWeight: 700, fontSize: 12, color: '#0f172a' }}>{f.label}</span>
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8' }}>{f.platform}</div>
              <div style={{
                marginTop: 6, fontSize: 10, fontWeight: 700,
                color: '#f59e0b',
              }}>
                🔜 COMING SOON
              </div>
            </div>
          ))}
        </div>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
